import { CARD_RULES, CHART_COLORS } from '../constants';

/**
 * Utility functions for deck management
 */

// Helper function to get max copies allowed for a card
export const getMaxCopiesAllowed = (cardNumber) => {
  if (!cardNumber) return 4;
  const upperCardNum = cardNumber.toUpperCase();
  const matchingKey = Object.keys(CARD_RULES).find(key => key.toUpperCase() === upperCardNum);
  return matchingKey ? CARD_RULES[matchingKey] : 4;
};

// Helper function to get image URL (direct access)
export const getProxyImageUrl = (imageUrl) => {
  return imageUrl || '';
};

/**
 * Validate deck and calculate statistics
 * @param {Array} cards - Array of card objects in the deck
 * @returns {Object} - { errors: string[], stats: object }
 */
export const validateDeck = (cards) => {
  const errors = [];
  const stats = {
    totalCards: 0,
    cardsByLevel: {},
    cardsByLevelAndCharacter: {}, // Track level distribution per character
    sceneCards: 0,
    cardCounts: {},
    characterBreakdown: {}
  };

  // Count cards and track duplicates
  cards.forEach(card => {
    const count = parseInt(card.count) || 1;
    stats.totalCards += count;

    // Check for duplicate limit using cardNumber as unique identifier
    const uniqueCardId = card.cardNumber || card.number || card.id;
    if (!stats.cardCounts[uniqueCardId]) {
      stats.cardCounts[uniqueCardId] = 0;
    }
    stats.cardCounts[uniqueCardId] += count;

    // Extract level from type or card.level field
    const typeText = typeof card.type === 'string' ? card.type : card.type?.value || '';
    const featureText = typeof card.feature === 'string' ? card.feature : card.feature?.value || '';
    const isScene = featureText.toLowerCase() === 'scene' || typeText.toLowerCase().includes('scene');
    
    if (isScene) {
      stats.sceneCards += count;
      if (!stats.characterBreakdown['Scene']) {
        stats.characterBreakdown['Scene'] = 0;
      }
      stats.characterBreakdown['Scene'] += count;
    } else {
      // Get level from card.level field or extract from type
      let level = card.level;
      if (!level) {
        const levelMatch = typeText.match(/level\s*(\d+)|lv\.?\s*(\d+)|l(\d+)/i);
        level = levelMatch ? (levelMatch[1] || levelMatch[2] || levelMatch[3]) : null;
      }

      // Categorize by character (moved up for level tracking)
      const characterName = card.characterName || 'Unknown';
      const isSpecialCharacter = ['BELIAL', 'JUGGULUS JUGGLER'].includes(characterName.toUpperCase());
      
      if (level) {
        const levelKey = `level${level}`;
        stats.cardsByLevel[levelKey] = (stats.cardsByLevel[levelKey] || 0) + count;
        
        // Track level by character
        if (!stats.cardsByLevelAndCharacter[levelKey]) {
          stats.cardsByLevelAndCharacter[levelKey] = {};
        }
        stats.cardsByLevelAndCharacter[levelKey][characterName] = 
          (stats.cardsByLevelAndCharacter[levelKey][characterName] || 0) + count;
      }
      
      let category;
      if (characterName.toLowerCase().includes('kaiju') || 
          (!isSpecialCharacter && typeText.toLowerCase().includes('kaiju'))) {
        category = 'Ultra Kaijus';
      } else {
        category = isSpecialCharacter ? characterName : characterName;
      }

      if (!stats.characterBreakdown[category]) {
        stats.characterBreakdown[category] = 0;
      }
      stats.characterBreakdown[category] += count;
    }
  });

  // Validate rules
  if (stats.totalCards !== 50) {
    errors.push(`⚠️ Deck has ${stats.totalCards} cards (need exactly 50)`);
  }

  Object.entries(stats.cardCounts).forEach(([cardId, count]) => {
    // cardId here is actually the cardNumber (e.g., PR-107), not the database id
    const card = cards.find(c => (c.cardNumber || c.number || c.id) === cardId);
    const cardNumber = cardId; // cardId is already the cardNumber
    const maxAllowed = getMaxCopiesAllowed(cardNumber);
    if (count > maxAllowed) {
      errors.push(`⚠️ "${card?.name || cardId}" has ${count} copies (max ${maxAllowed} allowed)`);
    }
  });

  return { errors, stats };
};

/**
 * Generate chart data for level distribution
 * @param {Object} stats - Deck validation stats
 * @returns {Object} - Chart.js data object with maxLevel for dynamic scaling
 */
export const getLevelChartData = (stats) => {
  const levels = Array.from({ length: 7 }, (_, i) => i + 1);
  const levelData = levels.map(level => {
    const key = `level${level}`;
    return stats.cardsByLevel[key] || 0;
  });
  
  // Calculate max for dynamic y-axis scaling
  const maxCount = Math.max(...levelData, 0);

  return {
    labels: levels.map(l => `L${l}`),
    datasets: [
      {
        label: 'Cards by Level',
        data: levelData,
        backgroundColor: '#3c618a',
        borderColor: '#2a4563',
        borderWidth: 1,
        borderRadius: 4,
      }
    ],
    maxCount // Pass max count for dynamic scaling
  };
};

/**
 * Generate chart data for level distribution with character breakdown
 * @param {Object} stats - Deck validation stats
 * @param {string|null} selectedCharacter - Filter by specific character, or null/"all" for all
 * @returns {Object} - Chart.js data object with stacked datasets per character
 */
export const getLevelChartDataByCharacter = (stats, selectedCharacter = null) => {
  const levels = Array.from({ length: 7 }, (_, i) => i + 1);
  
  // Get all unique characters from the level breakdown
  const allCharacters = new Set();
  levels.forEach(level => {
    const levelKey = `level${level}`;
    const charData = stats.cardsByLevelAndCharacter?.[levelKey] || {};
    Object.keys(charData).forEach(char => allCharacters.add(char));
  });
  
  const characters = Array.from(allCharacters).sort();
  
  // If filtering by specific character
  if (selectedCharacter && selectedCharacter !== 'all') {
    const levelData = levels.map(level => {
      const levelKey = `level${level}`;
      return stats.cardsByLevelAndCharacter?.[levelKey]?.[selectedCharacter] || 0;
    });
    
    const maxCount = Math.max(...levelData, 0);
    const charIndex = characters.indexOf(selectedCharacter);
    const color = CHART_COLORS[charIndex % CHART_COLORS.length];
    
    return {
      labels: levels.map(l => `L${l}`),
      datasets: [{
        label: selectedCharacter,
        data: levelData,
        backgroundColor: color,
        borderColor: color.replace(')', ', 0.8)').replace('rgb', 'rgba'),
        borderWidth: 1,
        borderRadius: 4,
      }],
      maxCount,
      characters
    };
  }
  
  // Show all characters with stacked bars
  const datasets = characters.map((char, index) => {
    const color = CHART_COLORS[index % CHART_COLORS.length];
    return {
      label: char,
      data: levels.map(level => {
        const levelKey = `level${level}`;
        return stats.cardsByLevelAndCharacter?.[levelKey]?.[char] || 0;
      }),
      backgroundColor: color,
      borderColor: '#fff',
      borderWidth: 1,
      borderRadius: 4,
    };
  });
  
  // Calculate max for stacked chart (sum of all characters per level)
  const maxCount = Math.max(...levels.map(level => {
    const levelKey = `level${level}`;
    return stats.cardsByLevel[levelKey] || 0;
  }), 0);
  
  return {
    labels: levels.map(l => `L${l}`),
    datasets,
    maxCount,
    characters
  };
};

/**
 * Generate chart data for character distribution
 * @param {Object} stats - Deck validation stats
 * @returns {Object} - Chart.js data object
 */
export const getCharacterChartData = (stats) => {
  const labels = Object.keys(stats.characterBreakdown);
  const data = Object.values(stats.characterBreakdown);

  return {
    labels: labels,
    datasets: [
      {
        label: 'Character Distribution',
        data: data,
        backgroundColor: CHART_COLORS.slice(0, labels.length),
        borderColor: '#fff',
        borderWidth: 2,
      }
    ]
  };
};

/**
 * Sort cards for display
 * @param {Array} cards - Array of card objects
 * @returns {Array} - Sorted array of cards
 */
export const sortCardsForDisplay = (cards) => {
  return [...cards].sort((a, b) => {
    const getCardType = (card) => {
      const feature = typeof card.feature === 'string' ? card.feature : (card.feature?.value || '');
      const featureText = (feature || '').toLowerCase();
      
      if (featureText.includes('ultra_hero')) return 'Ultra Hero';
      if (featureText.includes('ultra_mech')) return 'Ultra Mech';
      if (featureText.includes('kaiju')) return 'Kaiju';
      if (featureText === 'scene') return 'Scene';
      return 'Other';
    };

    const typeOrder = { 'Ultra Hero': 0, 'Ultra Mech': 1, 'Kaiju': 2, 'Scene': 3, 'Other': 4 };
    
    const typeA = getCardType(a);
    const typeB = getCardType(b);
    const typeCompare = typeOrder[typeA] - typeOrder[typeB];
    
    if (typeCompare !== 0) return typeCompare;
    
    // Sort by character name
    const charA = (a.characterName || '').toLowerCase();
    const charB = (b.characterName || '').toLowerCase();
    const charCompare = charA.localeCompare(charB);
    
    if (charCompare !== 0) return charCompare;
    
    // Sort by level (numeric)
    const levelA = parseInt(a.level || 0);
    const levelB = parseInt(b.level || 0);
    const levelCompare = levelA - levelB;
    
    if (levelCompare !== 0) return levelCompare;
    
    // Sort by card id
    return (a.number || '').localeCompare(b.number || '');
  });
};

/**
 * Normalize card data when adding from search
 * @param {Object} card - Raw card data from search
 * @returns {Object} - Normalized card object
 */
export const normalizeCard = (card) => {
  let level = card.level;
  if (!level && card.type) {
    const typeText = typeof card.type === 'string' ? card.type : card.type?.value || '';
    const levelMatch = typeText.match(/level\s*(\d+)|lv\.?\s*(\d+)|l(\d+)/i);
    level = levelMatch ? (levelMatch[1] || levelMatch[2] || levelMatch[3]) : null;
  }

  return {
    id: card.id,
    name: card.name,
    number: card.number,
    cardNumber: card.number,
    characterName: card.characterName || 'Unknown',
    rarity: typeof card.rarity === 'string' ? card.rarity : card.rarity?.value || '',
    type: typeof card.type === 'string' ? card.type : card.type?.value || '',
    feature: typeof card.feature === 'string' ? card.feature : card.feature?.value || '',
    level: level,
    images: card.images,
    count: 1
  };
};

/**
 * Shuffle array using Fisher-Yates algorithm
 * @param {Array} array - Array to shuffle
 * @returns {Array} - Shuffled array (mutates original)
 */
export const shuffleArray = (array) => {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
};

/**
 * Expand deck cards by count
 * @param {Array} cards - Array of card objects with count property
 * @returns {Array} - Expanded array with duplicates based on count
 */
export const expandDeckByCount = (cards) => {
  const fullDeck = [];
  cards.forEach(card => {
    const count = parseInt(card.count) || 1;
    for (let i = 0; i < count; i++) {
      fullDeck.push(card);
    }
  });
  return fullDeck;
};
